import { useId, useState } from "react";
import { Button, Textarea } from "@organizacaox/lai-design-system";
import { useQueryClient } from "@organizacaox/lai-design-system/query";
import { useTranslation } from "react-i18next";
import { updateLeadObservation, type LeadDados } from "@/api/leads";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";

export function LeadObservationField({
  leadId,
  value,
  editable,
}: {
  leadId: string;
  value?: string;
  editable: boolean;
}) {
  const client = useQueryClient();
  const { permissions } = useAccessSnapshot();
  const canEdit = editable && permissions.has("dashboard.dialer.dial");
  async function save(observation: string) {
    const filters = {
      predicate: (query: { queryKey: readonly unknown[] }) =>
        (query.queryKey[0] === "lead-dados" && query.queryKey[1] === leadId) ||
        (query.queryKey[0] === "whatsapp-incoming-lead-details" && query.queryKey[3] === leadId),
    };
    await client.cancelQueries(filters);
    const result = await updateLeadObservation(leadId, observation);
    await client.cancelQueries(filters);
    client.setQueriesData<LeadDados>(filters, (old) => {
      if (!old) return old;
      const dados = { ...old.dados };
      if (result.observation) dados.observacao_negocio = result.observation;
      else delete dados.observacao_negocio;
      return { ...old, dados };
    });
    return result.observation;
  }
  return (
    <LeadObservationEditor key={leadId} value={value ?? ""} editable={canEdit} onSave={save} />
  );
}

export function LeadObservationEditor({
  value,
  editable,
  onSave,
}: {
  value: string;
  editable: boolean;
  onSave: (value: string) => Promise<string>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!value && !editable) return null;
  const editing = draft !== null && editable;
  async function save() {
    if (draft === null || pending) return;
    setPending(true);
    setFailed(false);
    try {
      await onSave(draft);
      setDraft(null);
      setSaved(true);
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  }
  return (
    <section className="min-w-0 space-y-2 rounded-lg border border-border bg-muted/25 p-3 text-left">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={editing ? id : undefined}
          className="text-xs font-medium text-muted-foreground"
        >
          {t("leadData.labels.observacao_negocio")}
        </label>
        {editable && !editing && (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            onClick={() => {
              setDraft(value);
              setFailed(false);
              setSaved(false);
            }}
          >
            {t(value ? "leadData.observation.edit" : "leadData.observation.add")}
          </Button>
        )}
      </div>
      {editing ? (
        <>
          <Textarea
            id={id}
            value={draft}
            maxLength={10000}
            rows={5}
            disabled={pending}
            onChange={(event) => {
              setDraft(event.target.value);
              setFailed(false);
            }}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => {
                setDraft(null);
                setFailed(false);
              }}
            >
              {t("leadData.observation.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              loading={pending}
              loadingLabel={t("leadData.observation.saving")}
              disabled={pending || draft.trim() === value.trim()}
              onClick={() => void save()}
            >
              {t("leadData.observation.save")}
            </Button>
          </div>
          {failed && (
            <p role="alert" className="text-xs text-destructive">
              {t("leadData.observation.error")}
            </p>
          )}
        </>
      ) : value ? (
        <p className="whitespace-pre-wrap break-words text-sm">{value}</p>
      ) : null}
      {saved && !editing && (
        <p role="status" className="text-xs text-muted-foreground">
          {t("leadData.observation.saved")}
        </p>
      )}
    </section>
  );
}
